/**
 * VarAutocompleteMenu.tsx — presentational dropdown for inline ${ autocomplete.
 *
 * Positioned fixed at `rect` (caret bottom-left). Uses onMouseDown preventDefault
 * so the contentEditable never loses focus when the user clicks an item.
 */
import React from "react";
import { VarDef } from "./emailVars";
import { MenuState } from "./useVarAutocomplete";

// ---------------------------------------------------------------------------
// Inline styles (avoid extra styled-components dep; minimal surface)
// ---------------------------------------------------------------------------

const menuStyle: React.CSSProperties = {
	position: "fixed",
	zIndex: 300,
	background: "#fff",
	border: "1px solid #e3e8ef",
	borderRadius: 10,
	boxShadow: "0 12px 30px rgba(15,23,42,.2)",
	maxHeight: 240,
	overflowY: "auto",
	minWidth: 230,
	padding: 5,
};

function itemStyle(active: boolean): React.CSSProperties {
	return {
		display: "flex",
		flexDirection: "column",
		gap: 1,
		padding: "7px 9px",
		borderRadius: 7,
		cursor: "pointer",
		background: active ? "#e8f2fa" : "transparent",
	};
}

const nameStyle: React.CSSProperties = {
	fontSize: 12.5,
	color: "#1e293b",
	fontWeight: 600,
};

const sampleStyle: React.CSSProperties = {
	fontSize: 11,
	color: "#005e9e",
	fontFamily: "ui-monospace,Menlo,Consolas,monospace",
};

const emptyStyle: React.CSSProperties = {
	fontSize: 12,
	color: "#94a3b8",
	padding: "8px 10px",
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface VarAutocompleteMenuProps {
	menu: MenuState;
	pick: (i: number) => void;
	close: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function VarAutocompleteMenu({ menu, pick, close }: VarAutocompleteMenuProps): JSX.Element | null {
	if (!menu.open || !menu.rect) return null;

	const { rect, items, index } = menu;
	const left = Math.max(8, Math.min(rect.left, window.innerWidth - 250));
	const top = rect.bottom + 6;

	return (
		<div
			data-var-menu="1"
			style={{ ...menuStyle, left, top }}
			// Prevent mousedown from blurring the contentEditable
			onMouseDown={(e) => e.preventDefault()}>
			{items.length === 0 ? (
				<div style={emptyStyle}>Không có biến khớp</div>
			) : (
				items.map((v: VarDef, i: number) => (
					<div
						key={v.n}
						style={itemStyle(i === index)}
						onMouseDown={(e) => {
							e.preventDefault();
							pick(i);
						}}>
						<span style={nameStyle}>{v.d}</span>
						<span style={sampleStyle}>vd: {v.s || `\${${v.n}}`}</span>
					</div>
				))
			)}
		</div>
	);
}
